import React, {useState} from 'react'
import {Modal, ModalBody, ModalHeader} from 'react-bootstrap'
import {ToastContainer} from 'react-toastify'
import {aktivniUtovariFmkNaloga} from '../../../api/fmkNalozi'
import notifyToast from '../../../components/Toast/Toast'
import StepScanOrder from './steps/StepScanOrder'
import StepStartTransport from './steps/StepStartTransport'

type AktivniUtovar = {
    Id?: string | number
    id?: string | number
    brojNaloga?: string
    BrojNaloga?: string
    nalog?: string
    Nalog?: string
    uposlenik?: {
        UposlenikImePrezime?: string
    } | string
    Uposlenik?: string
    UposlenikImePrezime?: string
    startUtovara?: string
}

const getAktivniUtovariRows = (response: any): AktivniUtovar[] => {
    if (Array.isArray(response)) return response
    if (Array.isArray(response?.data)) return response.data
    if (Array.isArray(response?.aktivniUtovari)) return response.aktivniUtovari
    if (Array.isArray(response?.utovari)) return response.utovari

    return []
}

const getBrojNaloga = (item: AktivniUtovar) =>
    item.brojNaloga ?? item.BrojNaloga ?? item.nalog ?? item.Nalog ?? '-'

const getId = (item: AktivniUtovar) => item.Id ?? item.id ?? '-'

const getUposlenik = (item: AktivniUtovar) => {
    if (typeof item.uposlenik === 'string') return item.uposlenik

    return item.uposlenik?.UposlenikImePrezime ?? item.UposlenikImePrezime ?? item.Uposlenik ?? '-'
}

const formatDate24 = (date?: string) => {
    if (!date) return '-'

    const parsed = new Date(date.replace(' ', 'T'))

    if (Number.isNaN(parsed.getTime())) return date

    return parsed.toLocaleString('hr-BA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })
}

const getPocetakUtovara = (item: AktivniUtovar) =>
    formatDate24(item.startUtovara)


const TransportNaloga = () => {
    const [step, setStep] = useState(1)
    const [nalog, setNalog] = useState<string>('')
    const [aktivniUtovari, setAktivniUtovari] = useState<AktivniUtovar[]>([])
    const [aktivniUtovariModalOpen, setAktivniUtovariModalOpen] = useState(false)
    const [loadingAktivniUtovari, setLoadingAktivniUtovari] = useState(false)

    const openAktivniUtovari = async () => {
        setAktivniUtovariModalOpen(true)
        setLoadingAktivniUtovari(true)

        try {
            const response = await aktivniUtovariFmkNaloga()
            setAktivniUtovari(getAktivniUtovariRows(response))
        } catch (e: any) {
            notifyToast(false, e?.response?.data?.message ?? 'Greska prilikom ucitavanja aktivnih utovara.')
            setAktivniUtovari([])
        } finally {
            setLoadingAktivniUtovari(false)
        }
    }

    return (
        <div className='container-fluid p-2 p-md-4'>
            <ToastContainer/>

            <div
                className='d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-3 mb-3 p-3 bg-white border rounded'
                style={{
                    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
                }}
            >
                <div className='d-flex align-items-center gap-3'>
                    <div
                        className='d-flex align-items-center justify-content-center rounded bg-light'
                        style={{width: 42, height: 42}}
                    >
                        <i className='bi bi-truck text-primary fs-2'/>
                    </div>
                    <div>
                        <h1 className='text-gray-900 fw-bolder mb-0' style={{fontSize: 20, lineHeight: 1.2}}>
                            Utovar naloga
                        </h1>
                        <div className='text-muted fw-semibold fs-8'>
                            {step === 1 ? 'Skeniranje ili rucni unos naloga' : `Nalog #${nalog}`}
                        </div>
                    </div>
                </div>

                <div className='d-flex align-items-center gap-2'>
                    <span className='badge badge-light-primary fw-bold px-3 py-2'>
                        Korak {step} / 2
                    </span>
                    <button
                        type='button'
                        className='btn btn-sm btn-light-primary fw-bold'
                        onClick={openAktivniUtovari}
                        disabled={loadingAktivniUtovari}
                    >
                        <i className='bi bi-list-check me-2'/>
                        {loadingAktivniUtovari ? 'Ucitavanje...' : 'Aktivni utovari'}
                    </button>
                </div>
            </div>

            <div
                className='rounded'
                style={{
                    background: 'transparent',
                }}
            >

                {step === 1 && (
                    <StepScanOrder
                        onNext={(orderId: string) => {
                            setNalog(orderId)
                            setStep(2)
                        }}
                    />
                )}

                {step === 2 && (
                    <StepStartTransport
                        nalog={nalog}
                        onBack={() => setStep(1)}
                    />
                )}
            </div>

            <Modal
                className='modal-xl'
                show={aktivniUtovariModalOpen}
                onHide={() => setAktivniUtovariModalOpen(false)}
            >
                <ModalHeader
                    closeButton={true}
                    className='border-0 pb-0'
                >
                    <div>
                        <h2 className='fw-bolder mb-1'>Aktivni utovari</h2>
                        <div className='text-muted fw-semibold'>
                            Trenutno otvoreni utovari FMK naloga
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className='pt-5'>
                    {loadingAktivniUtovari ? (
                        <div className='text-center py-10 fw-semibold text-muted'>
                            <span className='spinner-border spinner-border-sm me-2'/>
                            Ucitavanje aktivnih utovara...
                        </div>
                    ) : aktivniUtovari.length > 0 ? (
                        <div className='table-responsive border rounded'>
                            <table className='table align-middle table-row-dashed gy-4 mb-0'>
                                <thead>
                                <tr className='fw-bold text-muted text-uppercase bg-light'>
                                    <th className='ps-5'>Id utovara</th>
                                    <th>Broj naloga</th>
                                    <th>Uposlenik</th>
                                    <th className='pe-5'>Pocetak utovara</th>
                                </tr>
                                </thead>
                                <tbody>
                                {aktivniUtovari.map((item, index) => (
                                    <tr key={`${getId(item)}-${getBrojNaloga(item)}-${index}`}>
                                        <td className='ps-5 fw-bold text-gray-800'>{getId(item)}</td>
                                        <td>
                                            <span className='badge badge-light-primary fw-bold'>
                                                {getBrojNaloga(item)}
                                            </span>
                                        </td>
                                        <td className='fw-semibold text-gray-700'>{getUposlenik(item)}</td>
                                        <td className='pe-5 text-muted fw-semibold'>{getPocetakUtovara(item)}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className='text-center py-10'>
                            <div className='fw-bold fs-4 text-gray-700 mb-2'>
                                Nema aktivnih utovara
                            </div>
                            <div className='text-muted'>
                                Trenutno nema naloga sa aktivnim utovarom.
                            </div>
                        </div>
                    )}
                </ModalBody>
            </Modal>

        </div>
    )
}

export default TransportNaloga
